import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Save, FileText } from "lucide-react";

import { api } from "@/lib/api";
import SupplierSection from "@/pages/invoice-review/SupplierSection";
import InvoiceDataSection from "@/pages/invoice-review/InvoiceDataSection";
import LineItemsSection from "@/pages/invoice-review/LineItemsSection";
import TotalsSection from "@/pages/invoice-review/TotalsSection";

/**
 * ManualInvoiceForm — form di ingresso manuale per creare un'autofattura
 * senza PDF né AI. Riusa gli stessi 4 componenti-sezione della `InvoiceReview`
 * (Supplier / InvoiceData / LineItems / Totals) così l'utente vede
 * ESATTAMENTE la stessa UI del flusso PDF, minus il PDF preview.
 *
 * Success flow:
 *   1. Utente compila il form
 *   2. Click "Crea autofattura" → POST /invoices/manual (consumo 1 credito)
 *   3. Backend salva in `status=extracted` + `source=manual`
 *   4. Redirect a `/app/invoices/{id}/review` per verificare e generare XML
 */

const genLineId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const emptyLine = () => ({
  id: genLineId(),
  descrizione: "",
  quantita: 1,
  prezzo_unitario: 0,
  prezzo_totale: 0,
  aliquota_iva: 22,
  natura: "",
});

const emptyExtracted = () => ({
  supplier_name: "",
  supplier_vat_id: "",
  supplier_address: "",
  supplier_city: "",
  supplier_zip: "",
  supplier_country: "",
  invoice_number: "",
  invoice_date: new Date().toISOString().slice(0, 10),
  data_ricezione: new Date().toISOString().slice(0, 10),
  data_autofattura: new Date().toISOString().slice(0, 10),
  currency: "EUR",
  original_currency: "",
  fx_rate: 1,
  fx_date: "",
  fx_source: "",
  total_amount: 0,
  total_amount_eur: 0,
  imponibile: 0,
  imposta: 0,
  aliquota_iva: 22,
  natura: "",
  service_description: "",
  is_service: true,
  is_eu_supplier: false,
  line_items: [emptyLine()],
});

export default function ManualInvoiceForm({ onPaywall }) {
  const navigate = useNavigate();
  const [extracted, setExtracted] = useState(emptyExtracted);
  const [docType, setDocType] = useState("TD17");
  const [saving, setSaving] = useState(false);
  // Suppliers anagrafica — fetched once via GET /suppliers (aggregated from
  // this user's past invoices, top 100). Enables the "Precompila da
  // anagrafica fornitori" picker inside SupplierSection.
  const [suppliers, setSuppliers] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/suppliers");
        setSuppliers(data || []);
      } catch (err) {
        // Non-critical — picker just stays hidden if the fetch fails.
        console.warn("[ManualForm] loadSuppliers failed:", err?.response?.data?.detail || err.message);
      }
    })();
  }, []);

  // Precompila i campi fornitore da un'anagrafica esistente (stessa
  // semantica di InvoiceReview::applySupplier).
  const applySupplier = (idxStr) => {
    const idx = parseInt(idxStr, 10);
    if (isNaN(idx) || !suppliers[idx]) return;
    const s = suppliers[idx];
    setExtracted((p) => ({
      ...p,
      supplier_name: s.supplier_name || p.supplier_name,
      supplier_vat_id: s.supplier_vat_id || p.supplier_vat_id,
      supplier_address: s.supplier_address || p.supplier_address,
      supplier_city: s.supplier_city || p.supplier_city,
      supplier_zip: s.supplier_zip || p.supplier_zip,
      supplier_country: s.supplier_country || p.supplier_country,
    }));
    toast.success(`Precompilato con dati di ${s.supplier_name}`);
  };

  // Field updater — matches the `upd("fieldName")` pattern used by the
  // shared Section components.
  const upd = (key) => (eOrValue) => {
    const raw = eOrValue?.target !== undefined ? eOrValue.target.value : eOrValue;
    setExtracted((p) => ({ ...p, [key]: raw }));
  };

  // Line item helpers — signature `(idx, key, value)` per matchare l'uso in
  // LineItemsSection.jsx (`updateLine(idx, "descrizione", e.target.value)`).
  // Il flusso PDF in InvoiceReview.jsx usa la stessa signature diretta e
  // sincronizza `prezzo_unitario` con `prezzo_totale` (perché nel form manuale
  // il quantitativo è sempre 1, non chiediamo unit price separato).
  const updateLine = (idx, key, value) => {
    setExtracted((p) => {
      const lines = [...(p.line_items || [])];
      if (!lines[idx]) return p;
      const next = { ...lines[idx], [key]: value };
      if (key === "prezzo_totale") next.prezzo_unitario = value;
      lines[idx] = next;
      return { ...p, line_items: lines };
    });
  };
  const addLine = () =>
    setExtracted((p) => ({
      ...p,
      line_items: [
        ...(p.line_items || []),
        { ...emptyLine(), aliquota_iva: p.aliquota_iva || 22 },
      ],
    }));
  const removeLine = (idx) =>
    setExtracted((p) => ({
      ...p,
      line_items: (p.line_items || []).filter((_, i) => i !== idx),
    }));

  // Auto-sync: imponibile = sum(line_items.prezzo_totale). This is the
  // same rule enforced server-side in the PDF flow.
  const sommaRighe = useMemo(() => {
    return (extracted.line_items || [])
      .reduce((s, li) => s + (parseFloat(li.prezzo_totale) || 0), 0);
  }, [extracted.line_items]);

  // Keep imponibile in sync with line totals (soft — user can still override
  // in the Totals section if the AI-style rounding differs by a few cents).
  React.useEffect(() => {
    const target = Number(sommaRighe.toFixed(2));
    setExtracted((p) => (Number(p.imponibile) === target ? p : { ...p, imponibile: target }));
  }, [sommaRighe]);

  // Auto-compute imposta + totale on change of imponibile / aliquota.
  React.useEffect(() => {
    const imp = parseFloat(extracted.imponibile) || 0;
    const al = parseFloat(extracted.aliquota_iva) || 0;
    const imposta = Number((imp * al / 100).toFixed(2));
    const total = Number((imp + imposta).toFixed(2));
    setExtracted((p) =>
      Number(p.imposta) === imposta && Number(p.total_amount) === total
        ? p
        : { ...p, imposta, total_amount: total, total_amount_eur: total }
    );
  }, [extracted.imponibile, extracted.aliquota_iva]);

  const canSubmit =
    !saving &&
    (extracted.supplier_name || "").trim().length > 0 &&
    Number(extracted.imponibile) > 0;

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Compila almeno nome fornitore e imponibile > 0");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/invoices/manual", {
        extracted,
        document_type: docType,
      });
      toast.success("Autofattura creata — verifica i dati e genera l'XML");
      navigate(`/app/invoices/${data.id}`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 402) {
        onPaywall?.(typeof detail === "string" ? detail : "Crediti esauriti");
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Errore nella creazione dell'autofattura");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="manual-invoice-form">
      <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-blue-100 grid place-items-center shrink-0">
          <FileText className="w-4 h-4 text-blue-700" />
        </div>
        <div className="text-sm text-blue-900">
          <div className="font-display font-semibold">Compila i dati manualmente</div>
          <p className="text-xs text-blue-800 mt-1 leading-relaxed">
            Utile quando il fornitore ti ha inviato solo un&apos;email o non hai il PDF originale.
            Verrà consumato <strong>1 credito</strong> come per un caricamento normale.
          </p>
        </div>
      </div>

      <SupplierSection
        extracted={extracted}
        upd={upd}
        suppliers={suppliers}
        applySupplier={applySupplier}
        hidePicker={suppliers.length === 0}
      />

      <div className="bg-white rounded-2xl shadow-sm border border-[#E8E4D9] p-5 sm:p-6">
        <InvoiceDataSection
          extracted={extracted}
          upd={upd}
          docType={docType}
          setDocType={setDocType}
          refreshFx={() => {}}
          refreshingFx={false}
        />
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-[#E8E4D9] p-5 sm:p-6">
        <LineItemsSection
          extracted={extracted}
          updateLine={updateLine}
          addLine={addLine}
          removeLine={removeLine}
          sommaRighe={sommaRighe}
        />
      </div>

      <TotalsSection extracted={extracted} upd={upd} />

      <div className="sticky bottom-0 z-10 bg-[#FAF9F6]/95 backdrop-blur border-t border-[#E8E4D9] px-1 py-4 flex items-center justify-between gap-3">
        <div className="text-xs text-zinc-500">
          {canSubmit ? "Pronto per la creazione" : "Completa i campi obbligatori"}
        </div>
        <button
          onClick={submit}
          disabled={!canSubmit}
          data-testid="manual-submit-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-zinc-900 text-white text-sm font-semibold hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Crea autofattura (1 credito)
        </button>
      </div>
    </div>
  );
}
